import { ShippingOption } from '@afterpaytouch/portal-api/types'
import { ShippingOptionErrors, ShippingOptionErrorType, ShippingOptionStatus, ShippingOptionValidationError } from '../../model'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { closeCheckout, getAllShippingOptions, getUpdatedShippingOption } from '..'
import { PURGE } from 'redux-persist'
import { updateCheckout } from '../checkout/actions/api'
import { compareObjects } from '../../utils/comparison'

export interface ShippingOptionState {
  allShippingOptions: ShippingOption[]
  xbtAllShippingOptions: ShippingOption[]
  selectedShippingOption: ShippingOption
  lastSelectedShippingOption: ShippingOption
  shippingOptionsStatus: ShippingOptionStatus
  error?: ShippingOptionErrorType
  validationError?: ShippingOptionValidationError
}

export const shippingOptionInitialState: ShippingOptionState = {
  allShippingOptions: [],
  xbtAllShippingOptions: [],
  // @ts-expect-error: OPERATION BLEED STOPPER
  selectedShippingOption: undefined,
  // @ts-expect-error: OPERATION BLEED STOPPER
  lastSelectedShippingOption: undefined,
  shippingOptionsStatus: ShippingOptionStatus.Initial,
  validationError: undefined,
}

const slice = createSlice({
  name: 'shippingOption',
  initialState: shippingOptionInitialState,
  reducers: {
    setSelectedShippingOption(state: ShippingOptionState, action: PayloadAction<ShippingOption>) {
      state.selectedShippingOption = action.payload
      state.lastSelectedShippingOption = action.payload
      state.shippingOptionsStatus = ShippingOptionStatus.UpdatingSelected
    },
    setShippingOptionError(state: ShippingOptionState, action: PayloadAction<ShippingOptionErrorType>) {
      state.error = action.payload
      state.shippingOptionsStatus = ShippingOptionStatus.Error
    },
    setShippingOptionStatus(state: ShippingOptionState, action: PayloadAction<ShippingOptionStatus>) {
      state.shippingOptionsStatus = action.payload
    },
    clearShippingOptionError(state: ShippingOptionState) {
      state.error = undefined
      state.shippingOptionsStatus = ShippingOptionStatus.Initial
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllShippingOptions.pending, (state, action) => {
        state.shippingOptionsStatus = ShippingOptionStatus.Loading
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.selectedShippingOption = undefined
        state.allShippingOptions = []
        state.xbtAllShippingOptions = []
        state.error = undefined
        state.validationError = undefined
      })
      .addCase(getAllShippingOptions.fulfilled, (state, action) => {
        if ('severity' in action.payload) {
          if (action.payload.severity === 'warn') {
            state.validationError = {
              severity: action.payload.severity,
              message: action.payload.message,
            }
            state.shippingOptionsStatus = ShippingOptionStatus.Loaded
            // @ts-expect-error: OPERATION BLEED STOPPER
            state.allShippingOptions = action.payload.allShippingOptions
            // @ts-expect-error: OPERATION BLEED STOPPER
            state.xbtAllShippingOptions = action.payload.xbtAllShippingOptions
          } else {
            state.shippingOptionsStatus = ShippingOptionStatus.Error
            state.error = ShippingOptionErrorType.Default
            state.validationError = action.payload
          }
        } else {
          const { allShippingOptions, xbtAllShippingOptions } = action.payload
          state.shippingOptionsStatus = ShippingOptionStatus.Loaded
          state.allShippingOptions = allShippingOptions
          state.xbtAllShippingOptions = xbtAllShippingOptions
          state.validationError = undefined
        }
      })
      .addCase(getAllShippingOptions.rejected, (state, action) => {
        state.shippingOptionsStatus = ShippingOptionStatus.Error
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.error = ShippingOptionErrors[action?.error?.message] ?? ShippingOptionErrorType.Default
        state.validationError = undefined
      })
      .addCase(getUpdatedShippingOption.pending, (state, action) => {
        state.error = undefined
        state.validationError = undefined
      })
      .addCase(getUpdatedShippingOption.fulfilled, (state, action) => {
        let doCheckoutUpdate = false
        if (typeof action.payload === 'undefined') {
          state.validationError = undefined

          return
        }

        if ('severity' in action.payload) {
          if (action.payload.severity === 'warn') {
            state.validationError = {
              severity: action.payload.severity,
              message: action.payload.message,
            }
          } else {
            state.shippingOptionsStatus = ShippingOptionStatus.Error
            state.error = (action?.payload?.errorCode as ShippingOptionErrorType) ?? ShippingOptionErrorType.Default
            state.validationError = action.payload
          }

          return
        }

        const { shippingOption, xbtShippingOption } = action.payload
        const matchingOptions = state.allShippingOptions.filter((option) => 'id' in shippingOption && option.id === shippingOption.id)

        if (matchingOptions.length < 1) {
          state.validationError = {
            severity: 'warn',
            message: `Shipping option with ID '${shippingOption.id}' could not be found.`,
          }
        } else if (matchingOptions.length > 1) {
          state.validationError = {
            severity: 'warn',
            message: `Shipping option with ID '${shippingOption.id}' was found more than once.`,
          }
        } else {
          matchingOptions[0].shippingAmount = shippingOption.shippingAmount
          matchingOptions[0].orderAmount = shippingOption.orderAmount

          if (typeof shippingOption.taxAmount !== 'undefined') {
            matchingOptions[0].taxAmount = shippingOption.taxAmount
          }

          if (typeof state.selectedShippingOption !== 'undefined' && state.selectedShippingOption.id === matchingOptions[0].id) {
            if (!compareObjects(state.selectedShippingOption, matchingOptions[0])) {
              doCheckoutUpdate = true
            }
            state.selectedShippingOption = matchingOptions[0]
          }

          if (xbtShippingOption) {
            const matchedXbtOptionIdx = state.xbtAllShippingOptions.findIndex((option) => option.id === xbtShippingOption.id)
            state.xbtAllShippingOptions[matchedXbtOptionIdx] = { ...state.xbtAllShippingOptions[matchedXbtOptionIdx], ...xbtShippingOption }
          }

          state.validationError = undefined
        }

        if (doCheckoutUpdate) {
          state.shippingOptionsStatus = ShippingOptionStatus.RequiresCheckoutUpdate
        } else {
          state.shippingOptionsStatus = ShippingOptionStatus.UpdatedSelected
        }
      })
      .addCase(getUpdatedShippingOption.rejected, (state, action) => {
        state.shippingOptionsStatus = ShippingOptionStatus.Error
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.error = ShippingOptionErrors[action?.error?.message] ?? ShippingOptionErrorType.Default
        state.validationError = undefined
      })
      .addCase(updateCheckout.pending, (state, action) => {
        state.error = undefined
      })
      .addCase(updateCheckout.rejected, (state, action) => {
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.selectedShippingOption = undefined
        state.error = ShippingOptionErrorType.ShippingOptionUnableToUpdate
        state.shippingOptionsStatus = ShippingOptionStatus.Error
      })
      .addCase(closeCheckout, () => shippingOptionInitialState)
      .addCase(PURGE, () => shippingOptionInitialState)
  },
})

export const { setSelectedShippingOption, setShippingOptionError, clearShippingOptionError, setShippingOptionStatus } = slice.actions

export default slice.reducer
